import { useEffect, useRef, useState } from "react";
import { api, HttpError } from "../api/client";
import { Logo } from "../components/Logo";

export interface SsoConfig {
  google: { clientId: string } | null;
  microsoft: { clientId: string; tenant: string } | null;
}

interface Props {
  onLoggedIn: (username: string) => void;
  sso: SsoConfig;
}

// Shared lazy-loader for third-party SDK scripts. Each src is loaded at most
// once across mounts, even under React strict-mode double-rendering.
const scriptLoaders = new Map<string, Promise<void>>();
function loadScript(src: string): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  const existing = scriptLoaders.get(src);
  if (existing) return existing;
  const p = new Promise<void>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`failed to load ${src}`));
    document.head.appendChild(s);
  });
  scriptLoaders.set(src, p);
  return p;
}

const GSI_SRC = "https://accounts.google.com/gsi/client";
// jsdelivr mirror of @azure/msal-browser. Pinned to a major version so the
// global `msal` API stays stable across patch releases.
const MSAL_SRC = "https://cdn.jsdelivr.net/npm/@azure/msal-browser@3/lib/msal-browser.min.js";

interface GsiResponse { credential?: string }
interface GoogleAccountsId {
  initialize: (opts: { client_id: string; callback: (r: GsiResponse) => void }) => void;
  renderButton: (parent: HTMLElement, opts: Record<string, unknown>) => void;
}
interface WindowWithGoogle extends Window {
  google?: { accounts?: { id?: GoogleAccountsId } };
}

interface MsalAuthResult {
  idToken?: string;
  account?: { username?: string };
}
interface MsalPublicClientApplication {
  initialize: () => Promise<void>;
  loginPopup: (req: { scopes: string[]; prompt?: string }) => Promise<MsalAuthResult>;
}
interface MsalNamespace {
  PublicClientApplication: new (cfg: {
    auth: { clientId: string; authority: string; redirectUri?: string };
  }) => MsalPublicClientApplication;
}
interface WindowWithMsal extends Window {
  msal?: MsalNamespace;
}

export function LoginScreen({ onLoggedIn, sso }: Props) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const googleSlot = useRef<HTMLDivElement | null>(null);
  const msInstance = useRef<MsalPublicClientApplication | null>(null);

  // — Google Identity Services button —
  useEffect(() => {
    if (!sso.google || !googleSlot.current) return;
    const clientId = sso.google.clientId;
    let cancelled = false;
    loadScript(GSI_SRC)
      .then(() => {
        if (cancelled || !googleSlot.current) return;
        const w = window as WindowWithGoogle;
        const id = w.google?.accounts?.id;
        if (!id) return;
        id.initialize({
          client_id: clientId,
          callback: async (resp) => {
            if (!resp.credential) return;
            setBusy(true);
            setError(null);
            try {
              const me = await api.googleLogin(resp.credential);
              onLoggedIn(me.username);
            } catch (err) {
              if (err instanceof HttpError && err.status === 403) {
                setError("This Google account is not permitted.");
              } else if (err instanceof HttpError && err.status === 401) {
                setError("Google sign-in was rejected.");
              } else {
                setError("Google sign-in failed. Try again.");
              }
            } finally {
              setBusy(false);
            }
          },
        });
        id.renderButton(googleSlot.current, {
          theme: "outline",
          size: "large",
          text: "signin_with",
          shape: "rectangular",
          width: 280,
        });
      })
      .catch(() => {
        if (!cancelled) setError("Could not load Google sign-in.");
      });
    return () => {
      cancelled = true;
    };
  }, [sso.google, onLoggedIn]);

  // — MSAL PublicClientApplication setup —
  // Init eagerly so the click latency on the button is just the popup itself.
  useEffect(() => {
    if (!sso.microsoft) return;
    const { clientId, tenant } = sso.microsoft;
    let cancelled = false;
    loadScript(MSAL_SRC)
      .then(() => {
        if (cancelled) return;
        const w = window as WindowWithMsal;
        const ns = w.msal;
        if (!ns) return;
        const app = new ns.PublicClientApplication({
          auth: {
            clientId,
            authority: `https://login.microsoftonline.com/${tenant}`,
            redirectUri: window.location.origin,
          },
        });
        app.initialize()
          .then(() => {
            if (!cancelled) msInstance.current = app;
          })
          .catch(() => {
            if (!cancelled) setError("Could not initialise Microsoft sign-in.");
          });
      })
      .catch(() => {
        if (!cancelled) setError("Could not load Microsoft sign-in.");
      });
    return () => {
      cancelled = true;
      msInstance.current = null;
    };
  }, [sso.microsoft]);

  const onMicrosoftClick = async () => {
    if (!msInstance.current || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await msInstance.current.loginPopup({
        scopes: ["openid", "email", "profile"],
        prompt: "select_account",
      });
      if (!res.idToken) {
        setError("Microsoft sign-in did not return a token.");
        return;
      }
      const me = await api.microsoftLogin(res.idToken);
      onLoggedIn(me.username);
    } catch (err) {
      if (err instanceof HttpError && err.status === 403) {
        setError("This Microsoft account is not permitted.");
      } else if (err instanceof HttpError && err.status === 401) {
        setError("Microsoft sign-in was rejected.");
      } else {
        setError("Microsoft sign-in failed. Try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const me = await api.login(username, password);
      onLoggedIn(me.username);
    } catch (err) {
      if (err instanceof HttpError && err.status === 401) {
        setError("Invalid username or password");
      } else {
        setError("Login failed. Try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  const ssoEnabled = !!sso.google || !!sso.microsoft;

  return (
    <div className="login-shell">
      <form className="login-card surface" onSubmit={submit}>
        <div className="login-brand">
          <span className="mark" style={{ color: "var(--fg)" }}>
            <Logo />
          </span>
          <span className="serif">Buaforsälgar</span>
        </div>
        <h2 className="serif">Sign in</h2>
        <label>
          <span className="eyebrow">Username</span>
          <input
            type="text"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoFocus
            required
          />
        </label>
        <label>
          <span className="eyebrow">Password</span>
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        {error && (
          <div className="login-error" role="alert">
            {error}
          </div>
        )}
        <button type="submit" className="login-submit" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
        {ssoEnabled && (
          <>
            <div className="login-divider" aria-hidden="true">or</div>
            {sso.google && <div ref={googleSlot} className="login-google" />}
            {sso.microsoft && (
              <button
                type="button"
                className="login-microsoft"
                onClick={onMicrosoftClick}
                disabled={busy}
              >
                <MsLogo />
                <span>Sign in with Microsoft</span>
              </button>
            )}
          </>
        )}
      </form>
    </div>
  );
}

function MsLogo() {
  // Official 4-square mark, drawn inline so we don't need an asset pipeline.
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1"  y="1"  width="9" height="9" fill="#f25022" />
      <rect x="11" y="1"  width="9" height="9" fill="#7fba00" />
      <rect x="1"  y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}
